import { Field, getIn, useFormikContext } from 'formik';
import { type FieldInputProps, type FieldMetaProps, type FormikState } from 'formik';
import { useCallback } from 'react';
import { type FocusEventHandler } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { CheckboxInput, ToggleInput } from '@hivebrite/design-system/Inputs';

import { shouldDisplayError } from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import { type CheckboxProps } from './types';
import { validateCheckbox } from './validate';

const Checkbox = ({
  ariaDescribedBy,
  ariaLabel,
  details,
  disabled = false,
  id,
  isToggle,
  info,
  infoIcon,
  label,
  labelMultiline,
  name,
  onBlur,
  onChange,
  onFocus,
  tooltip,
  validate,
  wrapperProps,
}: CheckboxProps) => {
  const htmlId = id || name;
  const Component = isToggle ? ToggleInput : CheckboxInput;
  const { required = false } = validate || {};

  const { handleBlur, setFieldValue, values } = useFormikContext();

  const handleBlurCb = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      handleBlur(e);
      onBlur?.(e);
    },
    [handleBlur, onBlur],
  );

  const handleChangeCb = useCallback(() => {
    const value = getIn(values, name);

    setFieldValue(name, !value);
    onChange?.(!value);
  }, [name, onChange, setFieldValue, values]);

  const handleValidate = useCallback(
    (value: boolean) =>
      validateCheckbox({
        isStringValue: false,
        required,
        value,
      }),
    [required],
  );

  return (
    <Box mb={6} {...wrapperProps}>
      <Field name={name} validate={handleValidate}>
        {({
          field,
          form,
          meta,
        }: {
          field: FieldInputProps<any>;
          form: FormikState<any>;
          meta: FieldMetaProps<any>;
        }) => (
          <Component
            {...field}
            {...(isToggle ? { checked: field.value } : {})}
            ariaDescribedBy={ariaDescribedBy}
            ariaLabel={ariaLabel}
            dataTestId="formik-checkbox"
            details={details}
            disabled={disabled}
            error={shouldDisplayError({ form, meta })}
            id={htmlId}
            info={info}
            infoIcon={infoIcon}
            label={label}
            labelMultiline={labelMultiline}
            onBlur={handleBlurCb}
            onChange={handleChangeCb}
            onFocus={onFocus}
            required={required}
            tooltip={tooltip}
          />
        )}
      </Field>
    </Box>
  );
};

export default Checkbox;
